import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { WorkOrderDetail, WorkOrderDetailResponse } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import {
  Alert,
  Button,
  Card,
  DefinitionList,
  Field,
  TextInput,
  InternalBadge,
  PageHeader,
  PriorityBadge,
  Spinner,
  StatusBadge,
  classes,
} from '../components/ui.js';
import { formatDate, formatDateTime, formatCurrency } from '../lib/format.js';
import { StatusActions } from '../workorder/StatusActions.js';
import { SUPPLIES_PAYMENT_METHOD_LABELS } from '../../shared/workflow.js';
import { PurgeWorkOrderButton } from '../workorder/PurgeWorkOrderButton.js';
import { InvestigationSection } from '../workorder/InvestigationSection.js';
import { ProposalSection } from '../workorder/ProposalSection.js';
import { PreApprovalSection } from '../workorder/PreApprovalSection.js';
import { ProcurementCard, ScheduleSection } from '../workorder/ScheduleSection.js';
import { CompletionSection } from '../workorder/CompletionSection.js';
import { CommentsSection } from '../workorder/CommentsSection.js';
import { AttachmentsSection } from '../workorder/AttachmentsSection.js';
import { TimelineSection } from '../workorder/TimelineSection.js';
import { AssignmentPanel } from '../workorder/AssignmentPanel.js';
import { EmergencyCalloutPanel } from '../workorder/EmergencyCalloutPanel.js';
import { NextStepsPanel } from '../workorder/NextStepsPanel.js';
import { CustomerNextStepsPanel } from '../workorder/CustomerNextStepsPanel.js';

type Tab = 'overview' | 'investigation' | 'proposals' | 'execution' | 'documents' | 'timeline';
const TABS: readonly Tab[] = [
  'overview',
  'investigation',
  'proposals',
  'execution',
  'documents',
  'timeline',
];

export function WorkOrderDetailPage() {
  const { id = '' } = useParams();
  const { can, isContractor, isCustomer } = useSession();
  // Emails and reminders deep-link straight to the tab that holds the next step.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState<Tab>(
    requestedTab && TABS.includes(requestedTab as Tab) ? (requestedTab as Tab) : 'overview',
  );
  // Deep links (?tab=) must win even when this page instance is reused for a new URL.
  useEffect(() => {
    if (requestedTab && TABS.includes(requestedTab as Tab)) setTab(requestedTab as Tab);
  }, [requestedTab]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['work-order', id],
    queryFn: () => apiRequest<WorkOrderDetailResponse>(`/api/work-orders/${id}`),
  });

  if (isLoading) return <Spinner label="Loading work order" />;

  if (error) {
    const notFound = error instanceof ApiRequestError && error.status === 404;
    return (
      <Alert
        variant="error"
        title={notFound ? 'Work order not found' : 'Could not load work order'}
      >
        {notFound
          ? 'This work order does not exist, or it is not one you are authorized to view.'
          : 'Please refresh the page and try again.'}{' '}
        <Link to="/work-orders" className="font-medium underline">
          Back to work orders
        </Link>
      </Alert>
    );
  }

  if (!data) return null;

  const { workOrder, availableTransitions } = data;

  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: 'overview', label: 'Overview', show: true },
    { id: 'investigation', label: 'Investigation', show: workOrder.kind !== 'supplies' },
    { id: 'proposals', label: 'Proposal', show: can(PERMISSIONS.PROPOSAL_VIEW) },
    {
      id: 'execution',
      label: workOrder.kind === 'supplies' ? 'Receipt & completion' : 'Schedule & completion',
      show: true,
    },
    { id: 'documents', label: 'Documents & comments', show: true },
    { id: 'timeline', label: 'History', show: true },
  ];

  return (
    <>
      <PageHeader
        title={workOrder.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold text-brand-800">
              {workOrder.workOrderNumber}
            </span>
            <StatusBadge status={workOrder.status} kind={workOrder.kind} />
            <PriorityBadge priority={workOrder.priority} />
            {workOrder.kind === 'supplies' && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                Supplies / consumables
              </span>
            )}
            <span className="text-sm text-slate-600">
              {workOrder.facilityCode} · {workOrder.facilityName}
            </span>
          </span>
        }
        actions={
          <>
            {workOrder.kind === 'supplies' &&
              isContractor &&
              can(PERMISSIONS.WORK_ORDER_CREATE) && (
                <Link
                  to={`/work-orders/new?repeat=${workOrder.id}`}
                  className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
                >
                  Repeat this order
                </Link>
              )}
            <StatusActions workOrder={workOrder} availableTransitions={availableTransitions} />
            <PurgeWorkOrderButton workOrder={workOrder} />
          </>
        }
      />

      {workOrder.emergencyCallout && (
        <div className="mb-4">
          <Alert variant="warning" title="Emergency call-out — work already performed">
            The proposal on this work order ratifies cost already incurred
            {workOrder.emergencyAuthorizedBy
              ? `; call-out authorized by ${workOrder.emergencyAuthorizedBy}`
              : ''}
            . USPS approval is still required; the three-quote requirement is waived.
          </Alert>
        </div>
      )}

      {workOrder.status === 'on_hold' && workOrder.holdReason && (
        <div className="mb-4">
          <Alert variant="warning" title="This work order is on hold">
            {workOrder.holdReason}
          </Alert>
        </div>
      )}

      {isContractor && can(PERMISSIONS.PROPOSAL_VIEW) && (
        <div className="mb-4">
          <NextStepsPanel
            workOrder={workOrder}
            availableTransitions={availableTransitions}
            onNavigate={setTab}
          />
        </div>
      )}
      {isCustomer && (
        <div className="mb-4">
          <CustomerNextStepsPanel workOrder={workOrder} onNavigate={setTab} />
        </div>
      )}

      <div className="mb-4 overflow-x-auto border-b border-slate-200">
        <nav className="-mb-px flex gap-1" aria-label="Work order sections">
          {tabs
            .filter((item) => item.show)
            .map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                aria-current={tab === item.id ? 'page' : undefined}
                className={classes(
                  'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium',
                  tab === item.id
                    ? 'border-brand-700 text-brand-800'
                    : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900',
                )}
              >
                {item.label}
              </button>
            ))}
        </nav>
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="flex flex-col gap-5 lg:col-span-2">
            <Card title={workOrder.kind === 'supplies' ? 'Items requested' : 'Problem description'}>
              <p className="whitespace-pre-wrap text-sm text-slate-800">
                {workOrder.kind === 'supplies'
                  ? (workOrder.itemsRequested ?? workOrder.problemDescription)
                  : workOrder.problemDescription}
              </p>
              {workOrder.kind === 'supplies' && workOrder.paymentMethod !== undefined && (
                <p className="mt-3 text-sm text-slate-700">
                  <span className="font-semibold">Payment method (VGS only):</span>{' '}
                  {workOrder.paymentMethod
                    ? SUPPLIES_PAYMENT_METHOD_LABELS[workOrder.paymentMethod]
                    : 'Not recorded'}
                </p>
              )}
              {workOrder.additionalComments && (
                <>
                  <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Additional comments
                  </h3>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">
                    {workOrder.additionalComments}
                  </p>
                </>
              )}
            </Card>

            <Card title="Request details">
              <DefinitionList
                items={[
                  {
                    label: 'Facility',
                    value: `${workOrder.facilityCode} — ${workOrder.facilityName}`,
                  },
                  {
                    label: 'Task order',
                    value: `${workOrder.taskOrderNumber} — ${workOrder.taskOrderName}`,
                  },
                  { label: 'Category', value: workOrder.categoryName },
                  { label: 'Building or area', value: workOrder.buildingArea },
                  { label: 'Specific location', value: workOrder.specificLocation },
                  { label: 'Equipment or asset', value: workOrder.equipmentAsset },
                  { label: 'Submitted', value: formatDateTime(workOrder.submittedAt) },
                  {
                    label: 'Requested completion',
                    value: formatDate(workOrder.requestedCompletionDate),
                  },
                  { label: 'Acknowledged by VGS', value: formatDateTime(workOrder.acknowledgedAt) },
                  {
                    label: 'Approved amount',
                    value:
                      workOrder.approvedTotalCents === null
                        ? null
                        : formatCurrency(workOrder.approvedTotalCents),
                  },
                ]}
              />
            </Card>

            <EmergencyCalloutPanel workOrder={workOrder} />

            <Card title="Point of contact">
              <DefinitionList
                items={[
                  { label: 'Name', value: workOrder.requestorName },
                  {
                    label: 'Email',
                    value: (
                      <a
                        className="text-brand-700 underline"
                        href={`mailto:${workOrder.requestorEmail}`}
                      >
                        {workOrder.requestorEmail}
                      </a>
                    ),
                  },
                  { label: 'Phone', value: workOrder.requestorPhone },
                ]}
              />
            </Card>
          </div>

          <div className="flex flex-col gap-5">
            {isContractor && (
              <Card
                title={
                  <span className="flex items-center gap-2">
                    <span className="text-base font-semibold text-slate-900">VGS assignment</span>
                    <InternalBadge />
                  </span>
                }
              >
                <AssignmentPanel workOrder={workOrder} />
              </Card>
            )}

            <Card title="Invoice">
              {isContractor ? (
                <InvoicePanel workOrder={workOrder} />
              ) : (
                <DefinitionList
                  columns={1}
                  items={[{ label: 'Invoice number', value: workOrder.invoiceNumber }]}
                />
              )}
            </Card>

            <Card title="Key dates">
              <DefinitionList
                columns={1}
                items={[
                  { label: 'Submitted', value: formatDateTime(workOrder.submittedAt) },
                  { label: 'Approved', value: formatDateTime(workOrder.approvedAt) },
                  { label: 'Completed', value: formatDateTime(workOrder.completedAt) },
                  { label: 'Closed', value: formatDateTime(workOrder.closedAt) },
                ]}
              />
            </Card>
          </div>
        </div>
      )}

      {tab === 'investigation' && <InvestigationSection workOrder={workOrder} />}
      {tab === 'proposals' && (
        <div className="flex flex-col gap-5">
          <PreApprovalSection workOrder={workOrder} />
          <ProposalSection workOrder={workOrder} />
        </div>
      )}
      {tab === 'execution' && (
        <div className="flex flex-col gap-5">
          {workOrder.kind !== 'supplies' && <ProcurementCard workOrder={workOrder} />}
          <ScheduleSection workOrder={workOrder} />
          <CompletionSection workOrder={workOrder} />
        </div>
      )}
      {tab === 'documents' && (
        <div className="flex flex-col gap-5">
          <AttachmentsSection workOrder={workOrder} />
          <CommentsSection workOrder={workOrder} />
        </div>
      )}
      {tab === 'timeline' && <TimelineSection workOrderId={workOrder.id} kind={workOrder.kind} />}
    </>
  );
}

/**
 * The invoice a work order was billed on. VGS records it, usually after closure, and
 * USPS reads it in the work order report the agreement requires.
 */
function InvoicePanel({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(workOrder.invoiceNumber ?? '');
  const [failed, setFailed] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrder.id}`, {
        method: 'PATCH',
        body: { invoiceNumber: value.trim() || null },
      }),
    onSuccess: () => {
      setFailed(null);
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
    },
    onError: (caught: unknown) => {
      setFailed(
        caught instanceof ApiRequestError ? caught.message : 'The invoice number was not saved.',
      );
    },
  });

  return (
    <div className="flex flex-col gap-3">
      {failed && <Alert variant="error">{failed}</Alert>}
      <Field label="Invoice number" htmlFor="invoiceNumber" hint="Visible to USPS.">
        <TextInput
          id="invoiceNumber"
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
      </Field>
      <div>
        <Button
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={value.trim() === (workOrder.invoiceNumber ?? '')}
        >
          Save invoice number
        </Button>
      </div>
    </div>
  );
}
